import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, FileText, Download, Star, ThumbsUp, XCircle,
  CalendarPlus, Mail, Trash2, CheckCircle2, Eye, RotateCcw,
} from "lucide-react";
import api, { formatError } from "@/lib/api";
import { markOpened, useOpenedDocs } from "@/lib/openedDocs";
import { valueLabel, fmtDate } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/pages/Applications";
import EvaluatorReviews from "@/components/EvaluatorReviews";
import AiReviewCard, { AiSideSummary } from "@/components/AiReview";
import AllDocumentsButton from "@/components/AllDocumentsButton";

// One answer of the form: label from ui.fields.<field>, stored values shown with their label.
export function DataRow({ field, app }) {
  const { t } = useTranslation();
  const value = app[field];
  return value ? (
    <div className="py-3 border-b border-border last:border-0 grid grid-cols-3 gap-4">
      <span className="text-sm text-muted-foreground">{t(`fields.${field}`)}</span>
      <span className="text-sm col-span-2 break-words">{valueLabel(value)}</span>
    </div>
  ) : null;
}

export const PERSONAL_FIELDS = ["address", "city", "country_of_origin", "residence_permit"];
export const ACADEMIC_FIELDS = ["university", "faculty", "study_program", "field_of_study", "degree_level", "semesters_studied",
  "program_duration", "remaining_exams", "gpa", "financial_support", "financial_support_institution", "previous_penda"];
export const FINANCIAL_FIELDS = ["parents_marital_status", "siblings", "parent_unemployed", "monthly_income", "vehicles"];

export const Section = ({ title, children }) => (
  <div className="bg-card border border-border rounded-xl p-6 mb-6">
    <h3 className="font-heading text-lg font-medium mb-3">{title}</h3>
    <div>{children}</div>
  </div>
);

export default function ApplicationDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const opened = useOpenedDocs();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ interview_date: "", interview_time: "", location: "", message: "" });
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState({ interview: "", rejection: "" });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [ai, setAi] = useState(null); // {review, evaluators} of the second reader

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/applications/${id}`);
      setApp(res.data);
    } catch {
      toast.error(t("admin.detail.not_found"));
      navigate("/admin/applications");
    }
  }, [id, navigate, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get(`/applications/${id}/ai-review`).then((r) => setAi(r.data)).catch(() => setAi({ review: null }));
  }, [id]);
  useEffect(() => {
    api.get("/message-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const openInvite = (open) => {
    setInviteOpen(open);
    if (open) {
      setInvite((prev) => ({
        ...prev,
        message: prev.message || app?.interview?.message || templates.interview || "",
      }));
    }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/applications/${id}/status`, { status });
      setApp((p) => ({ ...p, status }));
      toast.success(t("admin.detail.marked", { status: t(`status.${status}`) }));
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const removeApp = async () => {
    if (!window.confirm(t("admin.detail.confirm_delete"))) return;
    await api.delete(`/applications/${id}`);
    toast.success(t("admin.detail.deleted"));
    navigate("/admin/applications");
  };

  const resetEvaluation = async () => {
    if (!window.confirm(t("admin.detail.reset_confirm"))) return;
    try {
      const res = await api.post(`/applications/${id}/reset-evaluation`);
      toast.success(res.data.message);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || t("admin.detail.reset_failed"));
    }
  };

  const downloadFile = async (f) => {
    try {
      const res = await api.get(`/files/${f.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      markOpened(f.storage_path);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error(t("admin.detail.could_not_open_file"));
    }
  };

  const sendInvite = async () => {
    if (!invite.interview_date || !invite.interview_time) {
      toast.error(t("admin.detail.invite_fill"));
      return;
    }
    setSending(true);
    try {
      const res = await api.post(`/applications/${id}/invite`, invite);
      setApp((p) => ({ ...p, interview: res.data.interview }));
      setInviteOpen(false);
      if (res.data.email_sent) toast.success(t("admin.detail.invite_sent"));
      else toast.warning(res.data.message);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || t("admin.detail.invite_failed"));
    } finally {
      setSending(false);
    }
  };

  const openPreview = async () => {
    if (!invite.interview_date || !invite.interview_time) {
      toast.error(t("admin.detail.invite_fill"));
      return;
    }
    setPreviewing(true);
    try {
      const res = await api.post(`/applications/${id}/invite/preview`, invite);
      setPreview(res.data);
      setInviteOpen(false);
      setPreviewOpen(true);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || t("admin.detail.preview_failed"));
    } finally {
      setPreviewing(false);
    }
  };

  if (!app) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="animate-fade-in">
      <button onClick={() => navigate("/admin/applications")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> {t("admin.detail.back")}
      </button>

      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-3xl font-semibold tracking-tight">{app.name} {app.surname}</h1>
            <StatusBadge status={app.status} />
          </div>
          <p className="text-muted-foreground mt-1">{app.email} · {app.phone}</p>
          {app.confirmation_email && (
            <p className="text-xs mt-2 flex items-center gap-1.5" data-testid="confirmation-email-status">
              <Mail className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">{t("admin.detail.confirmation_email")}</span>
              <span className={
                app.confirmation_email.status === "sent" ? "text-primary font-medium"
                : app.confirmation_email.status === "failed" ? "text-destructive font-medium"
                : "text-muted-foreground font-medium"
              }>
                {t(`email_status.${app.confirmation_email.status}`, { defaultValue: app.confirmation_email.status })}
              </span>
            </p>
          )}
        </div>
        <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" onClick={removeApp} data-testid="delete-app-btn">
          <Trash2 className="w-4 h-4 mr-2" /> {t("admin.detail.delete")}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Main */}
        <div className="lg:col-span-8">
          <Section title={t("admin.detail.personal")}>
            {PERSONAL_FIELDS.map((f) => <DataRow key={f} field={f} app={app} />)}
          </Section>

          <Section title={t("admin.detail.academic")}>
            {ACADEMIC_FIELDS.map((f) => <DataRow key={f} field={f} app={app} />)}
          </Section>

          <Section title={t("admin.detail.financial")}>
            {FINANCIAL_FIELDS.map((f) => <DataRow key={f} field={f} app={app} />)}
          </Section>

          {app.additional_notes && (
            <Section title={t("admin.detail.notes")}>
              <p className="text-sm leading-relaxed text-muted-foreground">{app.additional_notes}</p>
            </Section>
          )}

          <Section title={t("admin.detail.documents", { count: (app.files || []).length })}>
            {(app.files || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("admin.detail.no_documents")}</p>
            ) : (
              <>
              <AllDocumentsButton path={`/applications/${id}/documents.pdf`} />
              <div className="grid sm:grid-cols-2 gap-3">
                {app.files.map((f, i) => {
                  const seen = opened.has(f.storage_path);
                  return (
                  <button key={i} onClick={() => downloadFile(f)} className={`flex items-center gap-3 border rounded-lg p-3 hover:border-primary transition-colors text-left ${seen ? "border-emerald-300 bg-emerald-50 hover:bg-emerald-100/70" : "border-border hover:bg-primary/5"}`} data-testid={`doc-${f.field}`} data-opened={seen}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${seen ? "bg-emerald-100" : "bg-secondary"}`}>
                      {seen ? <CheckCircle2 className="w-4 h-4 text-emerald-700" strokeWidth={1.5} /> : <FileText className="w-4 h-4 text-primary" strokeWidth={1.5} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-muted-foreground">{t(`documents.${f.field}.label`, { defaultValue: f.field })}</p>
                      <p className="text-sm truncate">{f.original_filename}</p>
                      {f.from_referee && <p className="text-xs text-primary truncate">{t("admin.detail.uploaded_by_referee", { name: f.from_referee.name })}</p>}
                      {seen && <p className="text-xs text-emerald-700">{t("admin.detail.opened")}</p>}
                    </div>
                    <Download className="w-4 h-4 text-muted-foreground shrink-0" />
                  </button>
                  );
                })}
              </div>
              </>
            )}
            {(app.references || []).filter((r) => r.status === "pending").map((r) => (
              <p key={r.slot} className="text-sm text-muted-foreground mt-3" data-testid={`pending-${r.slot}`}>
                {t("admin.detail.pending_reference", { slot: t(`documents.${r.slot}.label`), name: r.referee_name,
                  email: r.referee_email, date: fmtDate(r.sent_at) })}
              </p>
            ))}
          </Section>

          <EvaluatorReviews appId={app.id} />
          {ai && <AiReviewCard review={ai.review}
            compare={ai.evaluators ? { label: t("admin.ai.col_evaluators"), scores: ai.evaluators.scores, overall: ai.evaluators.overall } : null} />}
        </div>

        {/* Decision */}
        <div className="lg:col-span-4">
          <div className="sticky top-8 space-y-6">
            {ai?.review && <AiSideSummary review={ai.review} evaluators={ai.evaluators} />}
            {/* Actions */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h3 className="font-heading text-base font-medium mb-4">{t("admin.detail.decision")}</h3>
              <div className="grid grid-cols-1 gap-2">
                <Button variant={app.status === "shortlisted" ? "default" : "outline"} onClick={() => setStatus("shortlisted")} className="justify-start" data-testid="shortlist-btn">
                  <Star className="w-4 h-4 mr-2" /> {t("admin.detail.shortlist")}
                </Button>
                <Button variant={app.status === "recommended" ? "default" : "outline"} onClick={() => setStatus("recommended")} className="justify-start" data-testid="recommend-btn">
                  <ThumbsUp className="w-4 h-4 mr-2" /> {t("admin.detail.recommend")}
                </Button>
                <Button variant={app.status === "rejected" ? "destructive" : "outline"} onClick={() => setStatus("rejected")} className="justify-start" data-testid="reject-btn">
                  <XCircle className="w-4 h-4 mr-2" /> {t("admin.detail.reject")}
                </Button>
              </div>

              <div className="border-t border-border mt-4 pt-4">
                {app.status === "recommended" ? (
                  <>
                  <Dialog open={inviteOpen} onOpenChange={openInvite}>
                    <DialogTrigger asChild>
                      <Button className="w-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="open-invite-btn">
                        <CalendarPlus className="w-4 h-4 mr-2" /> {t("admin.detail.invite_open")}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader><DialogTitle>{t("admin.detail.invite_title")}</DialogTitle></DialogHeader>
                      <div className="space-y-4 py-2">
                        <div className="grid grid-cols-2 gap-4">
                          <div><Label className="text-sm mb-2 block">{t("admin.detail.date")}</Label><Input value={invite.interview_date} onChange={(e) => setInvite({ ...invite, interview_date: e.target.value })} placeholder={t("admin.detail.date_placeholder")} data-testid="invite-date" /></div>
                          <div><Label className="text-sm mb-2 block">{t("admin.detail.time")}</Label><Input value={invite.interview_time} onChange={(e) => setInvite({ ...invite, interview_time: e.target.value })} placeholder={t("admin.detail.time_placeholder")} data-testid="invite-time" /></div>
                        </div>
                        <p className="text-xs text-muted-foreground -mt-1">{t("admin.detail.invite_hint")}</p>
                        <div><Label className="text-sm mb-2 block">{t("admin.detail.message")}</Label><Textarea value={invite.message} onChange={(e) => setInvite({ ...invite, message: e.target.value })} rows={12} className="text-xs leading-relaxed" data-testid="invite-message" /></div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={openPreview} disabled={previewing || sending} data-testid="preview-invite-btn">
                          {previewing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("admin.detail.preparing")}</> : <><Eye className="w-4 h-4 mr-2" /> {t("admin.detail.preview")}</>}
                        </Button>
                        <Button onClick={sendInvite} disabled={sending} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="send-invite-btn">
                          {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("common.sending")}</> : <><Mail className="w-4 h-4 mr-2" /> {t("admin.detail.invite_send")}</>}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader><DialogTitle>{t("admin.detail.preview_title")}</DialogTitle></DialogHeader>
                      {preview && (
                        <div className="space-y-3">
                          <div className="text-sm bg-secondary/50 rounded-lg p-3 space-y-1" data-testid="invite-preview-meta">
                            <p><span className="text-muted-foreground">{t("admin.detail.preview_to")}</span><span className="font-medium">{preview.to}</span></p>
                            <p><span className="text-muted-foreground">{t("admin.detail.preview_subject")}</span><span className="font-medium">{preview.subject}</span></p>
                          </div>
                          <div className="border border-border rounded-lg overflow-hidden bg-white">
                            <iframe title={t("admin.detail.preview_title")} srcDoc={preview.html} sandbox="" className="w-full h-[440px]" data-testid="invite-preview-frame" />
                          </div>
                        </div>
                      )}
                      <DialogFooter>
                        <Button variant="outline" onClick={() => { setPreviewOpen(false); setInviteOpen(true); }} data-testid="preview-back-btn">
                          {t("admin.detail.preview_back")}
                        </Button>
                        <Button onClick={() => { setPreviewOpen(false); sendInvite(); }} disabled={sending} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="preview-send-btn">
                          <Mail className="w-4 h-4 mr-2" /> {t("admin.detail.invite_send")}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground text-center leading-relaxed" data-testid="invite-locked-hint">
                    <Trans i18nKey="admin.detail.invite_locked" components={{ b: <b /> }} />
                  </p>
                )}

                {app.interview && (
                  <div className="mt-4 text-sm bg-secondary/60 border border-primary/10 rounded-lg p-3">
                    <p className="flex items-center gap-2 font-medium text-primary"><CalendarCheckInline /> {t("admin.detail.interview_scheduled")}</p>
                    <p className="text-muted-foreground mt-1">{t("admin.detail.interview_at", { date: app.interview.interview_date, time: app.interview.interview_time })}</p>
                    {app.interview.location && <p className="text-muted-foreground">{app.interview.location}</p>}
                    <p className="text-xs mt-1">{t("admin.detail.interview_email", { status: t(`email_status.${app.interview.email_status}`, { defaultValue: app.interview.email_status }) })}</p>
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-border">
                  <Button variant="ghost" onClick={resetEvaluation} className="w-full text-muted-foreground hover:text-destructive" data-testid="reset-evaluation-btn">
                    <RotateCcw className="w-4 h-4 mr-2" /> {t("admin.detail.reset")}
                  </Button>
                  <p className="text-[11px] text-muted-foreground text-center mt-1 leading-relaxed">{t("admin.detail.reset_hint")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CalendarCheckInline() {
  return <CalendarPlus className="w-4 h-4" />;
}
